import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { Grid, IconButton } from '@material-ui/core'
import { ArrowUpward, ArrowDownward, Delete, Add } from '@material-ui/icons'
import SydLabel from '../../../../../../../commonDesign/SydLabel'
import SydSelect from '../../../../../../../commonDesign/SydSelect'
import SydInput from '../../../../../../../commonDesign/SydInput'
import SydButton from '../../../../../../../commonDesign/Button'
import Switch from '../../../../../../../molecules/Switch'

const modeOptions = [
  { label: 'Membership (group hierarchy)', value: 'membership' },
  { label: 'Value Source (DQL-driven options)', value: 'value-source' },
  { label: 'Mixed (combine membership and value-source)', value: 'mixed' },
  { label: 'Purpose (legacy — deprecated)', value: 'purpose' }
]

const displayOptions = [
  { label: 'Simple (picker only)', value: 'simple' },
  { label: 'Complex (type switcher + picker)', value: 'complex' }
]

const sizeOptions = [
  { label: 'Small', value: 'sm' },
  { label: 'Large', value: 'lg' }
]

function ScopeItem ({ scope, index, total, onUpdate, onRemove, onMoveUp, onMoveDown, scopeTypeOptions }) {
  const handleScopeTypeChange = useCallback((scopeType) => {
    const updated = { ...scope, scopeType }
    if (!scope.displayName) {
      updated.displayName = scopeType
    }
    if (!scope.labels?.allItems) {
      updated.labels = { ...(scope.labels || {}), allItems: `All ${scopeType}s` }
    }
    onUpdate(updated)
  }, [scope, onUpdate])

  return (
    <div style={{
      border: '1px solid var(--border-color)',
      borderRadius: 'var(--border-radius-sm)',
      padding: '12px',
      marginBottom: '8px'
    }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
        <span className='font-size-sm' style={{ fontWeight: 600 }}>
          {scope.displayName || scope.scopeType || '(unnamed)'}
        </span>
        <div style={{ display: 'flex', gap: '2px' }}>
          <IconButton size='small' disabled={index === 0} onClick={onMoveUp}>
            <ArrowUpward fontSize='small' />
          </IconButton>
          <IconButton size='small' disabled={index === total - 1} onClick={onMoveDown}>
            <ArrowDownward fontSize='small' />
          </IconButton>
          <IconButton size='small' onClick={onRemove}>
            <Delete fontSize='small' />
          </IconButton>
        </div>
      </div>
      <Grid container spacing={1}>
        <Grid item xs={6}>
          <SydLabel label='Scope Type' required>
            <SydSelect
              options={scopeTypeOptions}
              value={scope.scopeType || '__placeholder__'}
              onChange={(e) => {
                const val = e.target.value
                if (val !== '__placeholder__') handleScopeTypeChange(val)
              }}
            />
          </SydLabel>
        </Grid>
        <Grid item xs={6}>
          <SydLabel label='Display Name' helperText='Auto-filled from scope type; editable'>
            <SydInput
              value={scope.displayName || ''}
              onChange={(e) => onUpdate({ ...scope, displayName: e.target.value || undefined })}
              placeholder='e.g., By Advisor'
            />
          </SydLabel>
        </Grid>
        <Grid item xs={6}>
          <SydLabel label='Icon'>
            <SydInput
              value={scope.icon || ''}
              onChange={(e) => onUpdate({ ...scope, icon: e.target.value || null })}
              placeholder='Icon name'
            />
          </SydLabel>
        </Grid>
        <Grid item xs={6}>
          <SydLabel label='All Items Label' helperText='Auto-filled from scope type; editable'>
            <SydInput
              value={scope.labels?.allItems || ''}
              onChange={(e) => onUpdate({
                ...scope,
                labels: { ...(scope.labels || {}), allItems: e.target.value }
              })}
              placeholder='e.g., All Advisors'
            />
          </SydLabel>
        </Grid>
      </Grid>
    </div>
  )
}

ScopeItem.propTypes = {
  scope: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  total: PropTypes.number.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  onMoveUp: PropTypes.func.isRequired,
  onMoveDown: PropTypes.func.isRequired,
  scopeTypeOptions: PropTypes.array.isRequired
}

function PurposeItem ({ purpose, index, total, onUpdate, onRemove, onMoveUp, onMoveDown, allPurposes }) {
  const purposeOptions = useMemo(() => {
    const entries = Object.entries(allPurposes || {})
    return [
      { label: 'Select a purpose...', value: '__placeholder__' },
      ...entries.map(([code, data]) => ({
        label: `${data.displayName} (${code})`,
        value: code
      }))
    ]
  }, [allPurposes])

  return (
    <div style={{
      border: '1px solid var(--border-color)',
      borderRadius: 'var(--border-radius-sm)',
      padding: '12px',
      marginBottom: '8px'
    }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
        <span className='font-size-sm' style={{ fontWeight: 600 }}>
          {purpose.displayName || purpose.purposeCode || '(unnamed)'}
        </span>
        <div style={{ display: 'flex', gap: '2px' }}>
          <IconButton size='small' disabled={index === 0} onClick={onMoveUp}>
            <ArrowUpward fontSize='small' />
          </IconButton>
          <IconButton size='small' disabled={index === total - 1} onClick={onMoveDown}>
            <ArrowDownward fontSize='small' />
          </IconButton>
          <IconButton size='small' onClick={onRemove}>
            <Delete fontSize='small' />
          </IconButton>
        </div>
      </div>
      <Grid container spacing={1}>
        <Grid item xs={6}>
          <SydLabel label='Purpose Code' required>
            <SydSelect
              options={purposeOptions}
              value={purpose.purposeCode || '__placeholder__'}
              onChange={(e) => onUpdate({
                ...purpose,
                purposeCode: e.target.value === '__placeholder__' ? null : e.target.value
              })}
            />
          </SydLabel>
        </Grid>
        <Grid item xs={6}>
          <SydLabel label='Display Name' required>
            <SydInput
              value={purpose.displayName || ''}
              onChange={(e) => onUpdate({ ...purpose, displayName: e.target.value })}
              placeholder='e.g., Advisor View'
            />
          </SydLabel>
        </Grid>
      </Grid>
    </div>
  )
}

PurposeItem.propTypes = {
  purpose: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  total: PropTypes.number.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  onMoveUp: PropTypes.func.isRequired,
  onMoveDown: PropTypes.func.isRequired,
  allPurposes: PropTypes.object
}

function ValueSourceItem ({ scope, index, total, onUpdate, onRemove, onMoveUp, onMoveDown, allPurposes }) {
  const purposeOptions = useMemo(() => {
    const entries = Object.entries(allPurposes || {})
    return [
      { label: 'Select source purpose...', value: '__placeholder__' },
      ...entries.map(([code, data]) => ({
        label: `${data.displayName} (${code})`,
        value: code
      }))
    ]
  }, [allPurposes])

  const scopeTypeOptions = useMemo(() => {
    const sourcePurpose = allPurposes?.[scope.sourcePurposeCode]
    const preds = (sourcePurpose?.scopePredicates || [])
      .filter(p => p.type === 'expression')
    return [
      { label: 'Select scope type...', value: '__placeholder__' },
      ...preds.map(p => ({ label: p.scopeType, value: p.scopeType }))
    ]
  }, [allPurposes, scope.sourcePurposeCode])

  const columnOptions = useMemo(() => {
    const sourcePurpose = allPurposes?.[scope.sourcePurposeCode]
    const cols = sourcePurpose?.columns || []
    return [
      { label: 'Select column...', value: '__placeholder__' },
      ...cols.map(c => ({ label: c.name, value: c.name }))
    ]
  }, [allPurposes, scope.sourcePurposeCode])

  const labelColumnOptions = useMemo(() => {
    const sourcePurpose = allPurposes?.[scope.sourcePurposeCode]
    const cols = sourcePurpose?.columns || []
    return [
      { label: '(Same as value column)', value: '__placeholder__' },
      ...cols.map(c => ({ label: c.name, value: c.name }))
    ]
  }, [allPurposes, scope.sourcePurposeCode])

  const handleScopeTypeChange = useCallback((scopeType) => {
    const updated = { ...scope, scopeType }
    if (!scope.displayName) updated.displayName = scopeType
    if (!scope.labels?.allItems) {
      updated.labels = { ...(scope.labels || {}), allItems: `All ${scopeType}s` }
    }
    onUpdate(updated)
  }, [scope, onUpdate])

  return (
    <div style={{
      border: '1px solid var(--border-color)',
      borderRadius: 'var(--border-radius-sm)',
      padding: '12px',
      marginBottom: '8px'
    }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
        <span className='font-size-sm' style={{ fontWeight: 600 }}>
          {scope.displayName || scope.scopeType || '(unnamed)'}
        </span>
        <div style={{ display: 'flex', gap: '2px' }}>
          <IconButton size='small' disabled={index === 0} onClick={onMoveUp}>
            <ArrowUpward fontSize='small' />
          </IconButton>
          <IconButton size='small' disabled={index === total - 1} onClick={onMoveDown}>
            <ArrowDownward fontSize='small' />
          </IconButton>
          <IconButton size='small' onClick={onRemove}>
            <Delete fontSize='small' />
          </IconButton>
        </div>
      </div>
      <Grid container spacing={1}>
        <Grid item xs={6}>
          <SydLabel label='Source Purpose' required helperText='Purpose whose query returns the option list'>
            <SydSelect
              options={purposeOptions}
              value={scope.sourcePurposeCode || '__placeholder__'}
              onChange={(e) => onUpdate({
                ...scope,
                sourcePurposeCode: e.target.value === '__placeholder__' ? null : e.target.value,
                scopeType: '',
                valueColumn: null,
                labelColumn: null
              })}
            />
          </SydLabel>
        </Grid>
        <Grid item xs={6}>
          <SydLabel label='Scope Type' required helperText='Pick a scope dimension declared on the source purpose (expression-type predicates)'>
            <SydSelect
              options={scopeTypeOptions}
              value={scope.scopeType || '__placeholder__'}
              onChange={(e) => {
                const val = e.target.value
                if (val !== '__placeholder__') handleScopeTypeChange(val)
              }}
            />
          </SydLabel>
        </Grid>
        <Grid item xs={6}>
          <SydLabel label='Display Name'>
            <SydInput
              value={scope.displayName || ''}
              onChange={(e) => onUpdate({ ...scope, displayName: e.target.value || undefined })}
              placeholder='e.g., Owner'
            />
          </SydLabel>
        </Grid>
        <Grid item xs={6}>
          <SydLabel label='Value Column' required>
            <SydSelect
              options={columnOptions}
              value={scope.valueColumn || '__placeholder__'}
              onChange={(e) => onUpdate({
                ...scope,
                valueColumn: e.target.value === '__placeholder__' ? null : e.target.value
              })}
            />
          </SydLabel>
        </Grid>
        <Grid item xs={6}>
          <SydLabel label='Label Column' helperText='Optional; defaults to the value column'>
            <SydSelect
              options={labelColumnOptions}
              value={scope.labelColumn || '__placeholder__'}
              onChange={(e) => onUpdate({
                ...scope,
                labelColumn: e.target.value === '__placeholder__' ? null : e.target.value
              })}
            />
          </SydLabel>
        </Grid>
        <Grid item xs={6}>
          <SydLabel label='All Items Label'>
            <SydInput
              value={scope.labels?.allItems || ''}
              onChange={(e) => onUpdate({
                ...scope,
                labels: { ...(scope.labels || {}), allItems: e.target.value }
              })}
              placeholder='e.g., All Owners'
            />
          </SydLabel>
        </Grid>
      </Grid>
    </div>
  )
}

ValueSourceItem.propTypes = {
  scope: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  total: PropTypes.number.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  onMoveUp: PropTypes.func.isRequired,
  onMoveDown: PropTypes.func.isRequired,
  allPurposes: PropTypes.object
}

const mixedSourceTypeOptions = [
  { label: 'Membership', value: 'membership' },
  { label: 'Value Source', value: 'valueSource' }
]

function MixedScopeItem ({ scope, index, total, onUpdate, onRemove, onMoveUp, onMoveDown, allPurposes }) {
  const sourceType = scope?.source?.type || 'membership'

  const purposeOptions = useMemo(() => {
    const entries = Object.entries(allPurposes || {})
    return [
      { label: 'Select source purpose...', value: '__placeholder__' },
      ...entries.map(([code, data]) => ({
        label: `${data.displayName} (${code})`,
        value: code
      }))
    ]
  }, [allPurposes])

  const membershipScopeTypeOptions = useMemo(() => {
    if (sourceType !== 'membership' || !scope?.source?.purposeCode) {
      return [{ label: 'Select scope type...', value: '__placeholder__' }]
    }
    const sourcePurpose = allPurposes?.[scope.source.purposeCode]
    const preds = (sourcePurpose?.scopePredicates || [])
      .filter(p => p.type === 'membership')
    return [
      { label: 'Select scope type...', value: '__placeholder__' },
      ...preds.map(p => ({ label: p.scopeType, value: p.scopeType }))
    ]
  }, [sourceType, scope?.source?.purposeCode, allPurposes])

  const valueSourceScopeTypeOptions = useMemo(() => {
    if (sourceType !== 'valueSource' || !scope?.source?.purposeCode) {
      return [{ label: 'Select scope type...', value: '__placeholder__' }]
    }
    const sourcePurpose = allPurposes?.[scope.source.purposeCode]
    const preds = (sourcePurpose?.scopePredicates || [])
      .filter(p => p.type === 'expression')
    return [
      { label: 'Select scope type...', value: '__placeholder__' },
      ...preds.map(p => ({ label: p.scopeType, value: p.scopeType }))
    ]
  }, [sourceType, scope?.source?.purposeCode, allPurposes])

  const valueColumnOptions = useMemo(() => {
    if (sourceType !== 'valueSource' || !scope?.source?.purposeCode) {
      return [{ label: 'Select column...', value: '__placeholder__' }]
    }
    const sourcePurpose = allPurposes?.[scope.source.purposeCode]
    const cols = sourcePurpose?.columns || []
    return [
      { label: 'Select column...', value: '__placeholder__' },
      ...cols.map(c => ({ label: c.name, value: c.name }))
    ]
  }, [sourceType, scope?.source?.purposeCode, allPurposes])

  const labelColumnOptions = useMemo(() => {
    const sourcePurpose = allPurposes?.[scope?.source?.purposeCode]
    const cols = sourcePurpose?.columns || []
    return [
      { label: '(Same as value column)', value: '__placeholder__' },
      ...cols.map(c => ({ label: c.name, value: c.name }))
    ]
  }, [scope?.source?.purposeCode, allPurposes])

  const handleSourceTypeChange = useCallback((nextType) => {
    if (nextType === sourceType) return
    if (nextType === 'membership') {
      onUpdate({
        ...scope,
        source: { type: 'membership', purposeCode: scope?.source?.purposeCode || null }
      })
    } else {
      onUpdate({
        ...scope,
        source: {
          type: 'valueSource',
          purposeCode: scope?.source?.purposeCode || null,
          valueColumn: null,
          labelColumn: null
        }
      })
    }
  }, [scope, sourceType, onUpdate])

  return (
    <div style={{
      border: '1px solid var(--border-color)',
      borderRadius: 'var(--border-radius-sm)',
      padding: '12px',
      marginBottom: '8px'
    }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
        <span className='font-size-sm' style={{ fontWeight: 600 }}>
          {scope.displayName || scope.scopeType || '(unnamed)'}
          <span style={{ marginLeft: '8px', color: 'var(--text-color-subtle)', fontWeight: 400 }}>
            ({sourceType === 'valueSource' ? 'Value Source' : 'Membership'})
          </span>
        </span>
        <div style={{ display: 'flex', gap: '2px' }}>
          <IconButton size='small' disabled={index === 0} onClick={onMoveUp}>
            <ArrowUpward fontSize='small' />
          </IconButton>
          <IconButton size='small' disabled={index === total - 1} onClick={onMoveDown}>
            <ArrowDownward fontSize='small' />
          </IconButton>
          <IconButton size='small' onClick={onRemove}>
            <Delete fontSize='small' />
          </IconButton>
        </div>
      </div>
      <Grid container spacing={1}>
        <Grid item xs={6}>
          <SydLabel label='Source Type' required>
            <SydSelect
              options={mixedSourceTypeOptions}
              value={sourceType}
              onChange={(e) => handleSourceTypeChange(e.target.value)}
            />
          </SydLabel>
        </Grid>
        <Grid item xs={6}>
          <SydLabel label='Source Purpose' required>
            <SydSelect
              options={purposeOptions}
              value={scope?.source?.purposeCode || '__placeholder__'}
              onChange={(e) => {
                const purposeCode = e.target.value === '__placeholder__' ? null : e.target.value
                if (sourceType === 'membership') {
                  onUpdate({
                    ...scope,
                    scopeType: '',
                    source: { type: 'membership', purposeCode }
                  })
                } else {
                  onUpdate({
                    ...scope,
                    scopeType: '',
                    source: {
                      type: 'valueSource',
                      purposeCode,
                      valueColumn: null,
                      labelColumn: null
                    }
                  })
                }
              }}
            />
          </SydLabel>
        </Grid>

        {sourceType === 'membership' && (
          <Grid item xs={6}>
            <SydLabel label='Scope Type' required helperText='Pick a membership predicate on the source purpose'>
              <SydSelect
                options={membershipScopeTypeOptions}
                value={scope.scopeType || '__placeholder__'}
                onChange={(e) => {
                  const val = e.target.value === '__placeholder__' ? '' : e.target.value
                  const updated = { ...scope, scopeType: val }
                  if (!scope.displayName) updated.displayName = val
                  if (!scope.labels?.allItems) {
                    updated.labels = { ...(scope.labels || {}), allItems: val ? `All ${val}s` : '' }
                  }
                  onUpdate(updated)
                }}
              />
            </SydLabel>
          </Grid>
        )}

        {sourceType === 'valueSource' && (
          <>
            <Grid item xs={6}>
              <SydLabel label='Scope Type' required helperText='Pick a scope dimension declared on the source purpose (expression-type predicates)'>
                <SydSelect
                  options={valueSourceScopeTypeOptions}
                  value={scope.scopeType || '__placeholder__'}
                  onChange={(e) => {
                    const val = e.target.value === '__placeholder__' ? '' : e.target.value
                    const updated = { ...scope, scopeType: val }
                    if (!scope.displayName) updated.displayName = val
                    if (!scope.labels?.allItems) {
                      updated.labels = { ...(scope.labels || {}), allItems: val ? `All ${val}s` : '' }
                    }
                    onUpdate(updated)
                  }}
                />
              </SydLabel>
            </Grid>
            <Grid item xs={6}>
              <SydLabel label='Value Column' required>
                <SydSelect
                  options={valueColumnOptions}
                  value={scope?.source?.valueColumn || '__placeholder__'}
                  onChange={(e) => onUpdate({
                    ...scope,
                    source: {
                      ...scope.source,
                      valueColumn: e.target.value === '__placeholder__' ? null : e.target.value
                    }
                  })}
                />
              </SydLabel>
            </Grid>
            <Grid item xs={6}>
              <SydLabel label='Label Column' helperText='Optional; defaults to the value column'>
                <SydSelect
                  options={labelColumnOptions}
                  value={scope?.source?.labelColumn || '__placeholder__'}
                  onChange={(e) => onUpdate({
                    ...scope,
                    source: {
                      ...scope.source,
                      labelColumn: e.target.value === '__placeholder__' ? null : e.target.value
                    }
                  })}
                />
              </SydLabel>
            </Grid>
          </>
        )}

        <Grid item xs={6}>
          <SydLabel label='Display Name'>
            <SydInput
              value={scope.displayName || ''}
              onChange={(e) => onUpdate({ ...scope, displayName: e.target.value || undefined })}
              placeholder='e.g., Owner'
            />
          </SydLabel>
        </Grid>
        <Grid item xs={6}>
          <SydLabel label='All Items Label'>
            <SydInput
              value={scope.labels?.allItems || ''}
              onChange={(e) => onUpdate({
                ...scope,
                labels: { ...(scope.labels || {}), allItems: e.target.value }
              })}
              placeholder='e.g., All Owners'
            />
          </SydLabel>
        </Grid>
      </Grid>
    </div>
  )
}

MixedScopeItem.propTypes = {
  scope: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  total: PropTypes.number.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  onMoveUp: PropTypes.func.isRequired,
  onMoveDown: PropTypes.func.isRequired,
  allPurposes: PropTypes.object
}

function ScopeSelectorConfigEditor ({ config, allPurposes, onUpdate }) {
  const [localConfig, setLocalConfig] = useState(config)

  useEffect(() => {
    if (JSON.stringify(localConfig) !== JSON.stringify(config)) {
      onUpdate(localConfig)
    }
  }, [localConfig, config, onUpdate])

  const purposeOptions = useMemo(() => {
    const entries = Object.entries(allPurposes || {})
    return [
      { label: 'Select a purpose...', value: '__placeholder__' },
      ...entries.map(([code, data]) => ({
        label: `${data.displayName} (${code})`,
        value: code
      }))
    ]
  }, [allPurposes])

  const membershipPredicates = useMemo(() => {
    if (localConfig.mode !== 'membership' || !localConfig.purposeCode) return []
    const purposeData = allPurposes?.[localConfig.purposeCode]
    return (purposeData?.scopePredicates || [])
      .filter(sp => sp.type === 'membership')
  }, [localConfig.mode, localConfig.purposeCode, allPurposes])

  const allScopeTypes = useMemo(() => membershipPredicates.map(sp => sp.scopeType), [membershipPredicates])

  const usedScopeTypes = useMemo(() => {
    return new Set((localConfig.scopes || []).map(s => s.scopeType).filter(Boolean))
  }, [localConfig.scopes])

  const unusedScopeTypes = useMemo(() => {
    return allScopeTypes.filter(st => !usedScopeTypes.has(st))
  }, [allScopeTypes, usedScopeTypes])

  const handleModeChange = useCallback((mode) => {
    setLocalConfig(prev => {
      const base = {
        mode,
        display: prev.display || 'simple',
        multiSelect: prev.multiSelect ?? false,
        hideAvatars: prev.hideAvatars ?? false,
        size: prev.size || 'sm'
      }
      if (mode === 'membership') {
        return { ...base, purposeCode: prev.purposeCode || null, scopes: prev.scopes || [] }
      }
      if (mode === 'value-source') {
        return { ...base, scopes: [] }
      }
      if (mode === 'mixed') {
        return { ...base, scopes: [] }
      }
      return { ...base, purposes: prev.purposes || [] }
    })
  }, [])

  const handleScopeUpdate = useCallback((index, updated) => {
    setLocalConfig(prev => {
      const scopes = [...(prev.scopes || [])]
      scopes[index] = updated
      return { ...prev, scopes }
    })
  }, [])

  const handleScopeRemove = useCallback((index) => {
    setLocalConfig(prev => ({
      ...prev,
      scopes: (prev.scopes || []).filter((_, i) => i !== index)
    }))
  }, [])

  const handleScopeMove = useCallback((index, direction) => {
    setLocalConfig(prev => {
      const scopes = [...(prev.scopes || [])]
      const target = index + direction
      if (target < 0 || target >= scopes.length) return prev
      ;[scopes[index], scopes[target]] = [scopes[target], scopes[index]]
      return { ...prev, scopes }
    })
  }, [])

  const handleAddScope = useCallback((scopeType) => {
    setLocalConfig(prev => ({
      ...prev,
      scopes: [...(prev.scopes || []), {
        scopeType,
        displayName: scopeType,
        labels: { allItems: `All ${scopeType}s` }
      }]
    }))
  }, [])

  const handlePurposeItemUpdate = useCallback((index, updated) => {
    setLocalConfig(prev => {
      const purposes = [...(prev.purposes || [])]
      purposes[index] = updated
      return { ...prev, purposes }
    })
  }, [])

  const handlePurposeItemRemove = useCallback((index) => {
    setLocalConfig(prev => ({
      ...prev,
      purposes: (prev.purposes || []).filter((_, i) => i !== index)
    }))
  }, [])

  const handlePurposeItemMove = useCallback((index, direction) => {
    setLocalConfig(prev => {
      const purposes = [...(prev.purposes || [])]
      const target = index + direction
      if (target < 0 || target >= purposes.length) return prev
      ;[purposes[index], purposes[target]] = [purposes[target], purposes[index]]
      return { ...prev, purposes }
    })
  }, [])

  const handleAddPurposeItem = useCallback(() => {
    setLocalConfig(prev => ({
      ...prev,
      purposes: [...(prev.purposes || []), { purposeCode: null, displayName: '' }]
    }))
  }, [])

  const handleAddValueSourceScope = useCallback(() => {
    setLocalConfig(prev => ({
      ...prev,
      scopes: [...(prev.scopes || []), {
        scopeType: '',
        displayName: '',
        labels: { allItems: '' },
        sourcePurposeCode: null,
        valueColumn: null,
        labelColumn: null
      }]
    }))
  }, [])

  const handleAddMixedMembership = useCallback(() => {
    setLocalConfig(prev => ({
      ...prev,
      scopes: [...(prev.scopes || []), {
        scopeType: '',
        displayName: '',
        labels: { allItems: '' },
        source: { type: 'membership', purposeCode: null }
      }]
    }))
  }, [])

  const handleAddMixedValueSource = useCallback(() => {
    setLocalConfig(prev => ({
      ...prev,
      scopes: [...(prev.scopes || []), {
        scopeType: '',
        displayName: '',
        labels: { allItems: '' },
        source: { type: 'valueSource', purposeCode: null, valueColumn: null, labelColumn: null }
      }]
    }))
  }, [])

  const mode = localConfig.mode || 'membership'

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <SydLabel required label='Mode' helperText='How scope options are derived'>
          <SydSelect
            options={modeOptions}
            value={mode}
            onChange={(e) => handleModeChange(e.target.value)}
          />
        </SydLabel>
      </Grid>

      {mode === 'membership' && (
        <>
          <Grid item xs={12}>
            <SydLabel required label='Source Purpose' helperText='Purpose whose scopePredicates define available scopes'>
              <SydSelect
                options={purposeOptions}
                value={localConfig.purposeCode || '__placeholder__'}
                onChange={(e) => setLocalConfig(prev => ({
                  ...prev,
                  purposeCode: e.target.value === '__placeholder__' ? null : e.target.value
                }))}
              />
            </SydLabel>
          </Grid>

          <Grid item xs={12}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <h4 style={{ margin: 0 }}>Scopes</h4>
              <span style={{ fontSize: '0.875rem', color: 'var(--text-color-subtle)' }}>
                Configure which scopes to show and in what order. Leave empty to auto-derive from all membership predicates.
              </span>
            </div>
            {(localConfig.scopes || []).map((scope, i) => {
              const otherUsed = new Set(
                (localConfig.scopes || [])
                  .filter((_, j) => j !== i)
                  .map(s => s.scopeType)
                  .filter(Boolean)
              )
              const options = [
                { label: 'Select scope type…', value: '__placeholder__' },
                ...allScopeTypes
                  .filter(st => !otherUsed.has(st))
                  .map(st => ({ label: st, value: st }))
              ]
              return (
                <ScopeItem
                  key={i}
                  scope={scope}
                  index={i}
                  total={(localConfig.scopes || []).length}
                  scopeTypeOptions={options}
                  onUpdate={(updated) => handleScopeUpdate(i, updated)}
                  onRemove={() => handleScopeRemove(i)}
                  onMoveUp={() => handleScopeMove(i, -1)}
                  onMoveDown={() => handleScopeMove(i, 1)}
                />
              )
            })}
            {unusedScopeTypes.length > 0
              ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <SydSelect
                    options={[
                      { label: 'Add scope…', value: '__placeholder__' },
                      ...unusedScopeTypes.map(st => ({ label: st, value: st }))
                    ]}
                    value='__placeholder__'
                    onChange={(e) => {
                      const val = e.target.value
                      if (val !== '__placeholder__') handleAddScope(val)
                    }}
                    size='sm'
                  />
                </div>
              )
              : allScopeTypes.length > 0
                ? (
                  <span style={{ fontSize: '0.875rem', color: 'var(--text-color-subtle)' }}>
                    All available scope types have been added.
                  </span>
                )
                : null}
          </Grid>
        </>
      )}

      {mode === 'purpose' && (
        <Grid item xs={12}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <h4 style={{ margin: 0 }}>Purposes</h4>
            <span style={{ fontSize: '0.875rem', color: 'var(--text-color-subtle)' }}>
              Legacy mode — kept for backwards compatibility. New configurations should use Value Source or Mixed.
            </span>
          </div>
          {(localConfig.purposes || []).map((purpose, i) => (
            <PurposeItem
              key={i}
              purpose={purpose}
              index={i}
              total={(localConfig.purposes || []).length}
              allPurposes={allPurposes}
              onUpdate={(updated) => handlePurposeItemUpdate(i, updated)}
              onRemove={() => handlePurposeItemRemove(i)}
              onMoveUp={() => handlePurposeItemMove(i, -1)}
              onMoveDown={() => handlePurposeItemMove(i, 1)}
            />
          ))}
          <SydButton size='sm' variant='outline' icon={<Add />} onClick={handleAddPurposeItem}>
            Add Purpose
          </SydButton>
        </Grid>
      )}

      {mode === 'value-source' && (
        <Grid item xs={12}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <h4 style={{ margin: 0 }}>Value Sources</h4>
            <span style={{ fontSize: '0.875rem', color: 'var(--text-color-subtle)' }}>
              Each entry runs a source purpose query and exposes its distinct values as picker options.
            </span>
          </div>
          {(localConfig.scopes || []).map((scope, i) => (
            <ValueSourceItem
              key={i}
              scope={scope}
              index={i}
              total={(localConfig.scopes || []).length}
              allPurposes={allPurposes}
              onUpdate={(updated) => handleScopeUpdate(i, updated)}
              onRemove={() => handleScopeRemove(i)}
              onMoveUp={() => handleScopeMove(i, -1)}
              onMoveDown={() => handleScopeMove(i, 1)}
            />
          ))}
          <SydButton size='sm' variant='outline' icon={<Add />} onClick={handleAddValueSourceScope}>
            Add Value Source
          </SydButton>
        </Grid>
      )}

      {mode === 'mixed' && (
        <Grid item xs={12}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <h4 style={{ margin: 0 }}>Scopes</h4>
            <span style={{ fontSize: '0.875rem', color: 'var(--text-color-subtle)' }}>
              Each entry self-describes its source. Combine membership and value-source scopes within one picker.
            </span>
          </div>
          {(localConfig.scopes || []).map((scope, i) => (
            <MixedScopeItem
              key={i}
              scope={scope}
              index={i}
              total={(localConfig.scopes || []).length}
              allPurposes={allPurposes}
              onUpdate={(updated) => handleScopeUpdate(i, updated)}
              onRemove={() => handleScopeRemove(i)}
              onMoveUp={() => handleScopeMove(i, -1)}
              onMoveDown={() => handleScopeMove(i, 1)}
            />
          ))}
          <div style={{ display: 'flex', gap: '8px' }}>
            <SydButton size='sm' variant='outline' icon={<Add />} onClick={handleAddMixedMembership}>
              Add Membership
            </SydButton>
            <SydButton size='sm' variant='outline' icon={<Add />} onClick={handleAddMixedValueSource}>
              Add Value Source
            </SydButton>
          </div>
        </Grid>
      )}

      <Grid item xs={12}>
        <h4 style={{ margin: '0 0 8px 0' }}>Display Settings</h4>
      </Grid>

      <Grid item xs={6} md={4}>
        <SydLabel label='Display Mode' helperText='Simple shows picker only; Complex adds scope type switcher'>
          <SydSelect
            options={displayOptions}
            value={localConfig.display || 'simple'}
            onChange={(e) => setLocalConfig(prev => ({ ...prev, display: e.target.value }))}
          />
        </SydLabel>
      </Grid>

      <Grid item xs={6} md={4}>
        <SydLabel label='Size'>
          <SydSelect
            options={sizeOptions}
            value={localConfig.size || 'sm'}
            onChange={(e) => setLocalConfig(prev => ({ ...prev, size: e.target.value }))}
          />
        </SydLabel>
      </Grid>

      <Grid item xs={6} md={4}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', paddingTop: '4px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Switch
              checked={localConfig.multiSelect ?? false}
              onChange={(e) => setLocalConfig(prev => ({ ...prev, multiSelect: e.target.checked }))}
            />
            <span className='font-size-sm'>Multi-select</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Switch
              checked={localConfig.hideAvatars ?? false}
              onChange={(e) => setLocalConfig(prev => ({ ...prev, hideAvatars: e.target.checked }))}
            />
            <span className='font-size-sm'>Hide avatars</span>
          </div>
        </div>
      </Grid>
    </Grid>
  )
}

ScopeSelectorConfigEditor.propTypes = {
  config: PropTypes.object.isRequired,
  allPurposes: PropTypes.object.isRequired,
  onUpdate: PropTypes.func.isRequired
}

export default ScopeSelectorConfigEditor
